// Блок «Правила расчёта» в настройках: то, что заказчик меняет сам.
//
// ## Зачем он появился
//
// Значения лежали в базе с первой миграции, а поменять их можно было только запросом в базу.
// При этом договор обещает прямо: порядок согласования ведомости заказчик задаёт переключателем
// (тз, раздел 5.4.4), смещение официального периода и число недельных траншей — свои (5.4.5).
// То есть обещание «без доработки» было декоративным.
//
// ## Почему переключатель, число и рубли — один список
//
// Настройка приходит с сервера вместе с ВИДОМ значения, и орган управления выбирается по нему.
// Раскладывать список на три блока значило бы решать за сервер, что где лежит, и заводить
// четвёртый блок при каждой новой настройке.

import { useState } from 'react';

import type { SettingOut } from '@/shared/api/endpoints/settings';
import { useAuthStore } from '@/shared/auth/store';
import { formatKopecks } from '@/shared/money/format';
import { parseRublesToKopecks } from '@/shared/money/parse';
import { can } from '@/shared/rbac/permissions';
import * as Button from '@/shared/ui/button';
import { Block } from '@/shared/ui/block';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import { ScreenState, Skeleton } from '@/shared/ui/screen-state';
import { SelectField } from '@/shared/ui/select-field';
import * as Switch from '@/shared/ui/switch';
import { TextField } from '@/shared/ui/text-field';

import { useSaveSetting, useSettings } from './queries';

const ПРАВО_ПРАВИТЬ = 'core.setting.edit';

export function ПравилаРасчёта() {
  const настройки = useSettings();
  const права = useAuthStore((состояние) => состояние.permissions);
  const можно = can(права, ПРАВО_ПРАВИТЬ);

  return (
    <Block title='Правила расчёта' note='Меняются здесь, а не правкой кода: так обещано договором'>
      <ScreenState
        data={настройки.data}
        error={настройки.error}
        subject='настройки системы'
        isEmpty={(данные) => данные.items.length === 0}
        onRetry={() => void настройки.refetch()}
        loading={<Skeleton className='h-24 w-full' />}
        empty={
          <span className='text-paragraph-sm text-text-sub-600'>
            Настроек нет ни одной. Так не бывает: они заводятся миграцией, значит система развёрнута
            не до конца — скажите администратору.
          </span>
        }
      >
        {(данные) => (
          // Список, а не набор блоков: у каждой настройки СВОЁ действие над собой, и «Сохранить»
          // у шести полей — это шесть разных кнопок, а не шесть одинаковых. Ровно так же устроена
          // очередь приёмки, где «Принять» стоит у каждой заявки: разницу между ними задаёт
          // строка, а не подпись.
          <ul className='flex flex-col divide-y divide-stroke-soft-200'>
            {данные.items.map((настройка) => (
              <Настройка key={настройка.key} настройка={настройка} можно={можно} />
            ))}
          </ul>
        )}
      </ScreenState>
    </Block>
  );
}

function Настройка({ настройка, можно }: { настройка: SettingOut; можно: boolean }) {
  const сохранить = useSaveSetting();
  const [черновик, setЧерновик] = useState(() => показать(настройка));
  const [запрет, setЗапрет] = useState<string | null>(null);

  function отправить(значение: string) {
    if (!можно) {
      setЗапрет('Правила расчёта меняет администратор: они действуют на всех сразу.');
      return;
    }
    setЗапрет(null);
    сохранить.mutate({ key: настройка.key, value: значение });
  }

  // Переключатель подписывается САМ, полем подписи ему служит текст рядом: это тот же приём,
  // что в очереди приёмки и в мастере поста. У поля ввода подпись обязана быть настоящей —
  // пустая подпись означает поле без имени, которое читалка экрана называет «текстовое поле».
  if (настройка.value_kind === 'bool') {
    return (
      <li className='flex flex-col gap-1.5 py-3 first:pt-0 last:pb-0'>
        <label className='flex items-start justify-between gap-4'>
          <span className='flex min-w-0 flex-col gap-0.5'>
            <span className='text-label-sm text-text-strong-950'>{настройка.title}</span>
            {настройка.description ? (
              <span className='max-w-prose text-paragraph-xs text-text-sub-600'>
                {настройка.description}
              </span>
            ) : null}
          </span>
          <Switch.Root
            checked={настройка.value.toLowerCase() === 'true'}
            onCheckedChange={(включено) => отправить(включено ? 'true' : 'false')}
          />
        </label>
        {запрет !== null && <Hint.Root>{запрет}</Hint.Root>}
        {сохранить.isError && <ErrorAlert error={сохранить.error} />}
      </li>
    );
  }

  // Закрытый список значений — ВЫБОР, а не поле ввода. Пока стояло поле, человек видел в нём
  // наш код («accepted») и мог дописать к нему что угодно: значение не проверялось вовсе.
  const варианты = настройка.options ?? [];
  if (варианты.length > 0) {
    return (
      <li className='flex flex-col gap-1.5 py-3 first:pt-0 last:pb-0'>
        <div className='w-96'>
          <SelectField
            id={`setting-${настройка.key}`}
            label={настройка.title}
            value={настройка.value.replace(/^"|"$/g, '')}
            onChange={(значение) => отправить(значение)}
            options={варианты.map((вариант) => ({
              value: вариант.value,
              label: вариант.title,
            }))}
            placeholder='выберите значение'
            hint={настройка.description ?? undefined}
          />
        </div>
        {запрет !== null && <Hint.Root>{запрет}</Hint.Root>}
        {сохранить.isError && <ErrorAlert error={сохранить.error} />}
      </li>
    );
  }

  return (
    <li className='flex flex-col gap-1.5 py-3 first:pt-0 last:pb-0'>
      <div className='flex items-end gap-2'>
        <div className='w-72'>
          <TextField
            id={`setting-${настройка.key}`}
            label={настройка.title}
            value={черновик}
            onChange={setЧерновик}
            inputMode={настройка.value_kind === 'string' ? undefined : 'decimal'}
            hint={настройка.description ?? undefined}
          />
        </div>
        <Button.Root
          type='button'
          variant='neutral'
          mode='stroke'
          size='xsmall'
          onClick={() => отправить(вНаружу(настройка, черновик))}
        >
          {сохранить.isPending ? 'Сохраняем…' : 'Сохранить'}
        </Button.Root>
      </div>
      {запрет !== null && <Hint.Root>{запрет}</Hint.Root>}
      {сохранить.isError && <ErrorAlert error={сохранить.error} />}
    </li>
  );
}

/** Деньги показываем рублями, остальное — как пришло: копейки в поле человек не читает. */
function показать(настройка: SettingOut): string {
  if (настройка.value_kind !== 'money') {
    return настройка.value.replace(/^"|"$/g, '');
  }
  const копейки = Number(настройка.value);
  return Number.isFinite(копейки) ? formatKopecks(копейки) : настройка.value;
}

/** Обратно: рубли в копейки, остальное как есть. */
function вНаружу(настройка: SettingOut, черновик: string): string {
  if (настройка.value_kind !== 'money') {
    return черновик.trim();
  }
  const разбор = parseRublesToKopecks(черновик);
  return разбор.ok ? String(разбор.value) : черновик.trim();
}
